import { guid, DateRange, LQ } from 'rewire-common';
import { observable } from 'rewire-core';
import { ICell, IColumn, IGrid, IGridOptions, IRowData, createColumn, createGrid, IRow, IColumnOptions } from 'rewire-grid';
import deepequals from 'fast-deep-equal';
import {
  IBatchEmployeeTimecardResult,
  INameValue,
  IBatchEmployeeTimecard,
  IBatchEmployeeGroup,
  numberColumn,
  ScheduleFilterInput,
  ScheduleSort,
} from '../DataTypes';
import DataQueries from '../graphql/Queries';
import { IRouteManagerState, timecardSummaryRouteManager, payRouteManager } from '../main-navigation/RouteManager';
import PagingModel from '../settings/PagingModel';
import LinkCell from '../ui/LinkCell';
import { isSame } from '../utility/DateUtils';
import { formatCurrency } from '../utility/Formatters';
import DataMutations from '../graphql/Mutations';
import { TimecardStatusRenderer } from './CellRenderers';

interface ITimecardSummaryModelState {
  grid: IGrid;
  pagingModel: PagingModel;
  transitionStatus: string;
  searchResultsMessage: string;
  possibleTimecardTransitions: string[];
  grandTotals: IGrandTotals;
}

interface IGrandTotals {
  lastPageVisited: number;
  errors: number;
  gross: number;
  otherPay: number;
  reasons: INameValue[];
}

function formatFixed(value: number): string {
  if (!value) return '';
  return value.toString().includes('.') && value.toString().substr(value.toString().indexOf('.') + 1).length > 2
    ? value.toFixed(2)
    : String(value);
}

export default class TimecardSummaryBase {
  state: ITimecardSummaryModelState;
  DYNAMIC_COLUMN_STARTING_POSITION = 3;

  constructor() {
    const emptyResult = {
      results: [] as IBatchEmployeeTimecard[],
    } as IBatchEmployeeTimecardResult;
    this.state = observable({
      grid: this.createGrid(emptyResult),
      pagingModel: new PagingModel(this._load),
      searchResultsMessage: '',
      transitionStatus: 'none',
      possibleTimecardTransitions: [],
      grandTotals: {} as IGrandTotals,
    }) as ITimecardSummaryModelState;
    this.resetGrandTotals();
  }

  private shouldLoad(
    prevState: IRouteManagerState,
    route: string | undefined,
    effective: DateRange | undefined,
    filters: ScheduleFilterInput | undefined,
    groups: string[] | undefined,
    sorts?: ScheduleSort,
  ): boolean {
    if (prevState.route !== route) return true;
    if (!isSame(prevState.effective, effective)) return true;
    if (!deepequals(prevState.filters, filters)) return true;
    if (!deepequals(prevState.groups, groups)) return true;
    if (!deepequals(prevState.sorts, sorts)) return true;
    return false;
  }

  hasResults(results: IBatchEmployeeTimecardResult) {
    return results.results.length;
  }

  public get pagingModel() {
    return this.state.pagingModel;
  }

  public get transitionStatus() {
    return this.state.transitionStatus;
  }

  public get grid() {
    return this.state.grid;
  }

  public set grid(grid: IGrid) {
    this.state.grid = grid;
  }

  public get searchResultsMessage() {
    return this.state.searchResultsMessage;
  }

  public set searchResultsMessage(message: string) {
    this.state.searchResultsMessage = message;
  }

  public get possibleTimecardTransitions() {
    return this.state.possibleTimecardTransitions;
  }

  get isLoading(): boolean {
    return this.pagingModel.loading;
  }

  transitionTimecards = async (transition: string) => {
    const employeeIds = LQ(this.getAllDataRows())
      .map((r) => r.data.id)
      .filter((employeeId) => !!employeeId)
      .toArray();
    const { effective } = timecardSummaryRouteManager;
    if (employeeIds?.length > 0 && effective && effective.isValid) {
      try {
        this.pagingModel.loading = true;
        await DataMutations.transitionTimecards(employeeIds, effective, transition, true);
        await this._load();
      } finally {
        this.pagingModel.loading = false;
      }
    }
  };

  getAllDataRows(): IRow[] {
    return this.grid.rows.filter((r) => {
      if (r.data.employee === 'Grand Totals') return false;
      if (this.isGroupRow(r.cells.employee)) return false;
      return true;
    });
  }

  getGrandTotalRow(): IRow | undefined {
    return this.grid.rows.find((r) => r.data.employee === 'Grand Totals');
  }

  load = async (prevState: IRouteManagerState) => {
    const { route, effective, filters, groups, sorts } = timecardSummaryRouteManager;
    if (this.shouldLoad(prevState, route, effective, filters, groups, sorts)) {
      this.resetGrandTotals();
      await this.pagingModel.load();
    }
  };

  async loadPossibleTransitions() {
    this.state.possibleTimecardTransitions = await DataQueries.getPossibleTimecardTransitions('none');
  }

  _load = async (): Promise<{ cursor?: string; totalCount: number }> => {
    const { effective, filters, sorts, groups } = timecardSummaryRouteManager;
    if (!effective) {
      this.clearResults();
      return { cursor: '', totalCount: 0 };
    }

    try {
      const groupName: string | undefined = this.getGroupName(groups);
      this.pagingModel.itemsPerPage = groupName ? 5 : this.pagingModel.itemsPerPage === 5 ? 25 : this.pagingModel.itemsPerPage;

      if (!this.state.possibleTimecardTransitions?.length) {
        this.loadPossibleTransitions();
      }

      const results: IBatchEmployeeTimecardResult = await DataQueries.getEmployeeTimecardSummaries(
        effective,
        filters,
        groupName,
        sorts,
        this.pagingModel.itemsPerPage,
        this.pagingModel.currentPage,
      );
      this.searchResultsMessage = this.hasResults(results) ? '' : 'No results found that match the filter criteria';

      this.updateGrandTotals(results);
      if (this.shouldDisplayGrandTotals(results, this.pagingModel.itemsPerPage, this.pagingModel.page)) {
        const columns = results.columns;
        for (const reason of this.state.grandTotals.reasons) {
          const name = reason.name;
          if (!columns.includes(name)) {
            columns.push(name);
          }
        }
        this.updateGridColumns(columns);
      } else {
        this.updateGridColumns(results.columns);
      }
      const rows = this.createRows(results, this.grid.columns, groupName);
      this.grid.set(rows);
      return results.results.length ? { cursor: results.cursor, totalCount: results.totalCount } : { cursor: '', totalCount: 0 };
    } catch (ex) {
      this.searchResultsMessage = 'Error retrieving results';
      this.grid.set([]);
      return { cursor: '', totalCount: 0 };
    }
  };

  private getGroupName(groups: string[] = []): string | undefined {
    const groupName: string | undefined = groups.length ? groups[0] : undefined;
    switch (groupName) {
      // unsupported groupNames - do not create grouping rows
      case 'employee':
        return undefined;

      default:
        return groupName;
    }
  }

  private buildEmployeeColumnLink = (cell: ICell) => {
    if (cell.value === 'Grand Totals') return null;

    // TODO: if cell is from a group row, create a link that calls createPayPlanLinkFor({ selected property })
    // e.g. createPayPlanLinkFor({ batchName: cell.value })
    // where batchName is determined from the payRouteManager.groups collection (should )
    if (this.isGroupRow(cell)) {
      const groupName: string | undefined = payRouteManager.groups?.length ? payRouteManager.groups[0] : undefined;
      if (!groupName) return null;

      switch (groupName) {
        case 'area':
          return payRouteManager.createPayPlanLinkFor({
            filters: { areaIds: [cell.row.data.id] },
          });
        case 'group':
          return payRouteManager.createPayPlanLinkFor({
            filters: { groupIds: [cell.row.data.id] },
          });
        case 'occupation':
          return payRouteManager.createPayPlanLinkFor({
            filters: { occupationIds: [cell.row.data.id] },
          });
        case 'generator':
          return payRouteManager.createPayPlanLinkFor({
            filters: { generatorIds: [cell.row.data.id] },
          });
        case 'batch':
          return payRouteManager.createPayPlanLinkFor({
            filters: { batchIds: [cell.row.data.id] },
          });
        default:
          return null;
      }
    }
    return this.createEmployeeLink(cell);
  };

  protected createEmployeeLink(cell: ICell) {
    return payRouteManager.createPayPlanLinkFor({
      selectedEmployeeId: cell.row.data.id,
      selectedBatchId: undefined,
    });
  }

  private isGroupRow(cell: ICell): boolean {
    return cell.row.id.startsWith('GROUP-TOTALS');
  }

  private get columns(): any[] {
    return [
      {
        name: 'employee',
        title: 'Employee',
        width: '250px',
        renderer: LinkCell,
        fixed: true,
        type: { type: 'text', options: { to: this.buildEmployeeColumnLink } },
      },
      {
        name: 'status',
        title: 'Status',
        renderer: TimecardStatusRenderer,
        align: 'right',
      },
      {
        name: 'errors',
        title: 'Errors',
        align: 'right',
        map: (v: any) => (v ? v : undefined),
      },
      { name: 'gross', title: 'Gross', align: 'right', map: formatCurrency },
      {
        name: 'otherPay',
        title: 'Other Payments',
        align: 'right',
        map: formatCurrency,
      },
    ];
  }

  private clearResults() {
    this.grid.set([]);
    this.searchResultsMessage = '';
  }

  private createColumns(): IColumn[] {
    const cols: IColumn[] = [];

    for (const col of this.columns) {
      const { name, title, ...opts } = col;
      const column = createColumn(name, title, opts);
      column.canSort = false;
      cols.push(column);
    }
    return cols;
  }

  resetGrandTotals() {
    this.state.grandTotals.lastPageVisited = -1;
    this.state.grandTotals.errors = 0;
    this.state.grandTotals.gross = 0;
    this.state.grandTotals.otherPay = 0;

    if (this.state.grandTotals.reasons === undefined) {
      this.state.grandTotals.reasons = [];
    } else {
      this.state.grandTotals.reasons.set([]);
    }
  }

  updateGrandTotals(results: IBatchEmployeeTimecardResult) {
    const gt = this.state.grandTotals;
    if (gt.lastPageVisited >= this.pagingModel.page) {
      return;
    }
    let status = '';
    gt.lastPageVisited = this.pagingModel.page;
    for (const row of results.results) {
      if (row.timecardType === 'r') {
        continue;
      }
      gt.errors += row.errors ?? 0;
      gt.gross += row.gross ?? 0;
      gt.otherPay += row.otherPay ?? 0;
      if (status === '') {
        status = row.status;
      } else {
        if (status !== row.status) {
          status = 'mixed';
        }
      }

      for (const reason of row.reasons) {
        const gtReason = gt.reasons.find((r) => r.name === reason.name);
        if (gtReason === undefined) {
          gt.reasons.push({ name: reason.name, value: reason.value });
        } else {
          gtReason.value += reason.value || 0;
        }
      }
    }
    this.state.transitionStatus = status;
  }

  updateGridColumns(extraColumnNames: string[]) {
    const cols: IColumn[] = this.createColumns();

    if (extraColumnNames.length) {
      extraColumnNames.forEach((col: string, index: number) => {
        let colTitle = col;
        const idx = col.indexOf('0.0x');
        if (idx !== -1) {
          colTitle = col.substring(0, idx - 1);
        }

        const opts = { type: numberColumn };
        const column = createColumn(col, colTitle, opts as IColumnOptions);
        column.align = 'right';
        column.canSort = false;
        column.map = formatFixed;
        cols.splice(index + this.DYNAMIC_COLUMN_STARTING_POSITION, 0, column);
      });
      const options: IGridOptions = this.createOptions();
      this.grid = createGrid([], cols, options);
      this.grid.rowHeight = 36;
    }
  }

  createRows(results: IBatchEmployeeTimecardResult, cols: IColumn[], groupName?: string): IRowData[] {
    const rows: IRowData[] = [];

    if (results.results.length) {
      if (groupName && results.groups.length) {
        const sortedGroups = this.sortGroups(results.groups, groupName);
        for (const group of sortedGroups) {
          group.errors = 0;
          const rowsInGroup = results.results.filter((r: IBatchEmployeeTimecard) => r.groupByKeys.includes(group.id));
          for (const row of rowsInGroup) {
            const r: IRowData = this.processRow(row, cols, group);
            r.options = { cls: row.timecardType === 'r' ? 'reversal' : undefined };
            rows.push(r);
          }

          const groupTotalsRow: IRowData = this.createGroupTotalRow(group, results.groups);
          rows.push(groupTotalsRow);
        }
      } else {
        for (const row of results.results) {
          const r: IRowData = this.processRow(row, cols);
          r.options = { cls: row.timecardType === 'r' ? 'reversal' : undefined };
          // r.options = { cls: 'reversal' };
          rows.push(r);
        }
      }
      // convert to string to ensure it's not 'optimized' out by column mapper
      // (because 0 as a number is 'falsy' but not as a string and will not be displayed)
      if (this.shouldDisplayGrandTotals(results, this.pagingModel.itemsPerPage, this.pagingModel.page)) {
        const gt: IGrandTotals = this.state.grandTotals;
        const grandTotalRow: IRowData = {
          id: `${guid()}`,
          data: {
            employee: 'Grand Totals',
            errors: String(gt.errors || 0),
            gross: gt.gross,
            otherPay: gt.otherPay,
          },
          options: { cls: 'totalRow' },
        };
        for (const reason of gt.reasons) {
          if (grandTotalRow.data) {
            grandTotalRow.data[reason.name] = reason.value.toFixed(2);
          }
        }
        rows.push(grandTotalRow);
      }
    }
    return rows;
  }

  // only display Grand Totals if on the last page of all data available (and there is data to display)
  shouldDisplayGrandTotals(results: IBatchEmployeeTimecardResult, itemsPerPage: number, currentPage: number): boolean {
    if (results.results.length === 0) return false;

    const length = results.totalCount;
    let maxPages = length / itemsPerPage;
    if (maxPages === Math.floor(maxPages)) {
      // this.pagingModel.page is 0-based, so subtract 1 if the number of pages is exactly divisible by the itemsPerPage count
      maxPages--;
    } else {
      maxPages = Math.floor(maxPages);
    }
    return currentPage === maxPages;
  }

  private sortGroups(results: IBatchEmployeeGroup[], groupName?: string): IBatchEmployeeGroup[] {
    if (groupName) {
      results.sort((a: IBatchEmployeeGroup, b: IBatchEmployeeGroup) => a.name.localeCompare(b.name));
    }
    return results;
  }

  private createGroupTotalRow(group: IBatchEmployeeGroup, allGroups: IBatchEmployeeGroup[]): IRowData {
    const groupRow: IRowData = {
      id: `GROUP-TOTALS ${guid()}`,
      data: {
        id: group.id,
        employee: `${group.name} Totals`,
        errors: String(group.errors || 0),
        gross: String(group.gross || 0),
        otherPay: String(group.otherPay || 0),
      },
      options: { cls: 'groupTotalRow' },
    };

    // populate groupRow with 0 for every reason provided
    // not every group below will have a value for all groups, so provide a 0 (zero) for every possible case
    if (allGroups.length) {
      for (const g of allGroups) {
        if (g.reasons.length) {
          for (const r of g.reasons) {
            if (groupRow.data) {
              groupRow.data[r.name] = 0;
            }
          }
        }
      }
    }
    // update with actual values for those reasons that are provided in this group
    for (const g of group.reasons) {
      if (groupRow.data) {
        groupRow.data[g.name] = formatFixed(g.value);
      }
    }
    return groupRow;
  }

  // update totalRow with values from row
  // create & return new IRowData for grid
  private processRow(row: IBatchEmployeeTimecard, cols: IColumn[], group?: IBatchEmployeeGroup): IRowData {
    const r: IRowData = {
      id: `${guid()}`,
      data: row,
      options: (row as any).options,
    };

    for (const col of cols) {
      const fieldName = col.name;
      if (group !== undefined && fieldName === 'errors') {
        group.errors += row.errors;
      }

      const data = row.reasons.find((reason: INameValue) => reason.name === fieldName);
      if (data && r.data) {
        r.data[fieldName] = data.value || 0;
      }
    }
    return r;
  }

  private createOptions(): IGridOptions {
    return {
      clearSelectionOnBlur: false,
      readOnly: true,
      multiSelect: true,
      variableKeybinds: {
        'Ctrl+Insert': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+Delete': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
        'Ctrl+D': (_evt: React.KeyboardEvent<any>, _cell: ICell) => {},
      },
    };
  }

  private createGrid(results: IBatchEmployeeTimecardResult): IGrid {
    const cols: IColumn[] = this.createColumns();
    const rows: IRowData[] = this.createRows(results, cols);
    const options: IGridOptions = this.createOptions();
    const grid: IGrid = createGrid(rows, cols, options);
    grid.rowHeight = 36;
    return grid;
  }
}
